// The single rename dialog for the whole app.
//
// There used to be two near-identical copies — one in components/output/ (for
// hierarchy nodes) and one defined inline in Dashboard.jsx (for projects) — which
// had drifted apart in styling, validation, and whether they handled Escape. They
// shared testids but not behaviour, so a fix to one silently missed the other.
//
// ProjectOutput's header click-to-edit is deliberately NOT folded in here: it's an
// inline affordance rather than a modal, and it applies a slug transform this
// dialog must not. It shares the trim-and-reject-empty rule and nothing else.

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export default function RenameDialog({
  open,
  onOpenChange,
  title = "Rename",
  description,
  initialValue = "",
  maxLength = 80,
  submitLabel = "Save",
  /**
   * (trimmedValue) => void | Promise<void>
   * The dialog awaits it and stays open if it throws, so a failed API call
   * doesn't discard what the user typed.
   */
  onSubmit,
}) {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);

  // Re-seed on open so a previous edit (or a different target) doesn't linger.
  useEffect(() => {
    if (open) {
      setValue(initialValue || "");
      setSubmitting(false);
    }
  }, [open, initialValue]);

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit?.(trimmed);
      onOpenChange?.(false);
    } catch {
      // Left open on purpose — the caller surfaces the error via toast.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rename-dialog"
        className="max-w-[440px]"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 600 }}>{title}</DialogTitle>
          {/* Always rendered: Radix warns when a dialog has no description, and
              screen-reader users get nothing to explain what they're renaming. */}
          <DialogDescription style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {description || "Enter a new name, then save."}
          </DialogDescription>
        </DialogHeader>

        <input
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onOpenChange?.(false);
          }}
          data-testid="rename-input"
          maxLength={maxLength}
          style={{
            background: "var(--color-background-secondary)",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-primary)",
            fontSize: 14,
            padding: "10px 12px",
            borderRadius: 8,
            outline: "none",
            width: "100%",
            marginTop: 4,
          }}
        />

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            data-testid="rename-cancel"
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              fontSize: 13,
              padding: "8px 14px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="rename-submit"
            className="motion-micro"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              border: "none",
              fontSize: 13,
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: 8,
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
